import React, { useEffect } from "react";
import erpStyles from "../../pages/styles/ERPSolutions.module.css";
import heroImage from "../../assets/images/hero.webp";
import ServicesBanner from "../../components/common/ServicesBanner";
import UseCasesSection from "../../components/UseCasesSection";
import PageTitle from "../../components/PageTitle";
import CTASection from "../../components/common/CTASection";
import ServiceSection from "./ServiceSection";

// ── ERP Platforms ─────────────────────────────────────────
const erpTypes = [
  {
    icon: "fa-building",
    title: "SAP",
    description: "A global leader in enterprise software, SAP is built for organizations that need robust, scalable, and deeply integrated solutions. We help you harness SAP's powerful suite - including SAP S/4HANA - to manage finance, supply chain, manufacturing, HR, and analytics on a single intelligent platform. Ideal for mid-sized to large enterprises with complex, high-volume operations.",
  },
  {
    icon: "fa-cloud",
    title: "Oracle NetSuite",
    description: "The world's leading cloud-based ERP, Oracle NetSuite gives you a fully integrated suite for financials, CRM, e-commerce, inventory, and operations - all accessible anytime, anywhere. A perfect fit for fast-growing companies prioritizing flexibility, automation, and a true cloud-first approach. We handle setup, customization, and seamless integration with your existing tools.",
  },
  {
    icon: "fa-microsoft",
    title: "Microsoft Dynamics 365",
    description: "Microsoft Dynamics 365 combines ERP and CRM capabilities in one connected ecosystem, with native integration with Microsoft 365, Power BI, and Azure. Modular and highly adaptable - start with what you need and scale as you grow. An excellent choice for businesses already invested in the Microsoft environment who want a familiar, intelligent, unified platform.",
  },
  {
    icon: "fa-code-branch",
    title: "Open Source ERP",
    description: "For organizations seeking flexibility, cost control, and full ownership of their systems, open-source ERP platforms (such as Odoo, ERPNext, and others) offer a compelling alternative. Free from heavy licensing fees and highly customizable - ideal for startups, SMEs, and businesses with unique workflows. We provide expert implementation, custom module development, and ongoing support.",
  },
];

// ── Benefits ──────────────────────────────────────────────
const benefits = [
{
  icon: "fa-layer-group",
  title: "Centralized Data & Single Source of Truth",
  description: (
    <>
      ERP solutions bring all business data into one{" "}
      <span className={erpStyles.highlight}>unified system</span>,
      eliminating scattered spreadsheets and disconnected applications.
      Every department works from the same accurate,{" "}
      <span className={erpStyles.highlight}>real-time information</span>,
      reducing duplication and minimizing errors.
      Decision-makers gain instant access to
      {" "}<span className={erpStyles.highlight}>trustworthy data </span>without reconciling 
      conflicting reports.This creates better transparency, alignment, and smarter business decisions.
    </>
  ),
},

{
  icon: "fa-cogs",
  title: "Improved Efficiency & Automation",
  description:(
    <>ERP systems automate repetitive processes like invoicing, payroll, 
    inventory updates, and order management. This reduces manual effort, 
    minimizes human errors, and saves valuable time across departments.
    {" "}<span className={erpStyles.highlight}>Seamless workflows </span>improve 
    operational speed and eliminate unnecessary bottlenecks. As a result, businesses achieve higher productivity and efficiency  with the same resources. Operations become faster, smoother, more reliable, and highly scalable.
    </>
  ),  
},

  {
    icon: "fa-chart-bar",
    title: "Real-Time Insights & Reporting",
    description: (
      <>ERP platforms provide powerful dashboards and reporting tools that turn raw data into clear,{" "}
      <span className={erpStyles.highlight}> actionable insights. </span>
       Leaders gain instant visibility into sales, cash flow, inventory, and 
       performance metrics - all updated in real time. Customizable reports 
       help you spot trends and identify opportunities early. Rather than relying
       on outdated summaries, you make decisions based on what's truly happening right now.
        This enables faster responses and {" "}<span className={erpStyles.highlight}>smarter business planning.</span>
      </>
  ),},
  {
    icon: "fa-expand-arrows-alt",
    title: "Scalability & Business Growth",
    description: (
      <>A good ERP solution grows alongside your business at every stage. 
      Whether adding users, expanding locations, launching products, or entering new markets, 
      ERP scales smoothly to meet rising demands. Modular designs let you add functionality
      as needed, so you only invest in what you use today. By providing a stable, adaptable 
      foundation, ERP supports {" "}<span className={erpStyles.highlight}>sustainable growth </span>and helps you seize new opportunities quickly.
      This flexibility prevents costly system replacements in the future. 
      Your business can expand confidently without operational disruptions.
      </>
  ),},
  {
    icon: "fa-users",
    title: "Enhanced Collaboration & Productivity",
    description: (
      <>
      ERP connects all departments through a shared platform, improving {" "}
      <span className={erpStyles.highlight}> communication and teamwork </span>across the organization. Employees can access the 
      same real-time information whenever needed, reducing delays and misunderstandings. 
      This transparency streamlines approvals, {" "}<span className={erpStyles.highlight}>speeds up workflows </span>, and keeps projects moving efficiently. 
      Teams spend less time searching for data and more time delivering results. 
      The organization operates more smoothly with stronger collaboration and productivity.
      </>
  ),},
  {
    icon: "fa-dollar-sign",
    title: "Cost Savings & Better ROI",
    description: (
      <>
      While ERP is an investment, it delivers significant {" "}<span className={erpStyles.highlight}>long-term savings </span>. 
      By streamlining operations, reducing errors, and eliminating redundant 
      systems, ERP cuts operational costs steadily. Better inventory management 
      reduces waste, automation lowers labor costs, and improved forecasting prevents 
      costly mistakes. Consolidating multiple tools into one platform also reduces software expenses. 
      Together, these efficiencies deliver a strong return on investment - turning operational 
      excellence into real {" "}<span className={erpStyles.highlight}>financial gains </span>.
      </>
  ),},
];

//── Use Cases ─────────────────────────────────────────────
const useCases = [

  {
      challenge: (
      <>{" "}<span className={erpStyles.highlight}>Full-Scale Enterprise ERP & LMS Implementation</span>
      <br /><span className={erpStyles.highlight}>Client : Manufacturing & Industrial Solutions Provider.</span>
      <br />A legacy ERP with corrupted data and no security, large number of unmapped sales leads, non-compliant quotation formats, an unstructured financial system, and no centralized training or project management platform.
      </>
      ),
      solution: ["Over a 12-week deployment, DG FutureTech rebuilt and integrated the organization’s operations by streamlining lead management, GST/CIN-compliant quotations, financial controls, and training processes. We deployed a centralized ERP and LMS platform, validated the Chart of Accounts, and implemented infrastructure upgrades that improved collaboration, visibility, scalability, and operational efficiency."]
     },
  { 
    challenge: (
  <>
    <span className={erpStyles.highlight}>HRMS</span>
    <br /><span className={erpStyles.highlight}>Client: Growing Mid-Scale Organization</span>
    <br />
    Fragmented HR operations, partially manual record management, outsourced payroll and attendance processing, partially implementation performance tracking, and lack of role-based access - exposing sensitive data and delaying compliance reporting.
  </>
),
    solution: ["DG FutureTech deployed a fully integrated HRMS, centralizing onboarding, attendance, payroll, leave management, and performance evaluation into one platform. Role-based access, automated compliance reporting, employee self-service, and real-time HR dashboards improved operational visibility. Structured workflows and audit-ready documentation also supported the organization’s ISO certification requirements."]},

  {
      challenge: (
      <>{" "}<span className={erpStyles.highlight}>Streamlining Hospital Operations with HIMS</span> 
      <br /> The hospital struggled with fragmented patient records, manual appointment scheduling and billing, problems in stock maintenance, and disconnected workflows across departments which resulted in delays, revenue loss, and a poor patient experience.
      </>
      ),
      solution: ["DG FutureTech implemented a comprehensive HIMS that centralized patient records, appointment scheduling, billing, pharmacy operations, and inventory management. By connecting departments through a unified platform, the solution improved data accuracy, streamlined clinical and administrative workflows, enhanced service delivery, ensured regulatory compliance, and provided real-time visibility for better operational control."]
    }, 

];

// ── Component ─────────────────────────────────────────────
const ERPSolutions = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageTitle
        title="ERP Solutions"
        description="Enterprise Resource Planning solutions that unify your business processes - finance, inventory, HR, procurement, and more - into one integrated system."
      />
      <div className={erpStyles.pageWrapper}>

        <ServicesBanner
          title="ERP Solutions"
          heroImage={heroImage}
          compactMode={true}
          description="Streamline operations, empower teams, and drive growth - with a specialized ERP solution for every business need."
        />

        {/* ── 1. ERP PLATFORMS ── */}
        <section className={erpStyles.section}>
          <div className="container">
            <h2 className={erpStyles.sectionTitle}>
              <span>ERP Solutions We Offer</span>
            </h2>
            <p className={erpStyles.sectionSubtitle}>
              We work across leading ERP platforms, ensuring you get a solution
              matched to your industry, budget, and scale.
            </p>
            <div className={erpStyles.erpTypesGrid}>
              {erpTypes.map((item, i) => (
                <div key={i} className={erpStyles.erpTypeCard}>
                  {/* <div className={erpStyles.erpTypeIcon}>
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h3 className={erpStyles.erpTypeTitle}>{item.title}</h3>
                  <p className={erpStyles.erpTypeDescription}>{item.description}</p> */}
                  <div className={erpStyles.erpTypeHeader}>
                    <div className={erpStyles.erpTypeIcon}>
                    <i className={`fas ${item.icon}`}></i>
                    </div>
                  <h3 className={erpStyles.erpTypeTitle}>
                  {item.title}
                  </h3>
                  </div>
                  <p className={erpStyles.erpTypeDescription}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Not Sure CTA */}
            <div className={erpStyles.erpCta}>
              <div className={erpStyles.erpCtaContent}>
                <div>
                  <h3>Not Sure Which ERP Is Right for You?</h3>
                  <p>Our experts will assess your business needs and recommend the perfect solution</p>
                  <p>No guesswork, no pressure.</p>
                </div>
              </div>
              <a
                href="https://calendly.com/mitali-phatak-dgfuturetech/30min"
                target="_blank"
                rel="noopener noreferrer"
                className={erpStyles.erpCtaBtn}
              >
                Book a Free Consultation
              </a>
            </div>
          </div>
        </section>

        {/* ── 2. BENEFITS ── */}
        <section className={erpStyles.sectionAlt}>
          <div className="container">
            <h2 className={erpStyles.sectionTitle}>
              <span>Benefits of ERP Solutions</span>
            </h2>
            <p className={erpStyles.sectionSubtitle}>
              A well-implemented ERP transforms how your business operates -
              connecting people, processes, and data into one unified system.
            </p>
            <div className={erpStyles.benefitsGrid}>
              {benefits.map((item, i) => (
                <div key={i} className={erpStyles.benefitCard}>
                  <div className={erpStyles.benefitIcon}>
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h3 className={erpStyles.benefitTitle}>{item.title}</h3>
                  <p className={erpStyles.benefitDescription}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 3.Our ERP Solutions*/}
        <section className={erpStyles.section}>
          <div className="container">
            <h2 className={erpStyles.sectionTitle}>
              <span>ERP Solutions Offered by DG FutureTech</span>
            </h2>
            <h2 className={erpStyles.sectionTitle}>
              <h1>Our ERP Solutions</h1>
            </h2>
            <p className={erpStyles.sectionSubtitle}>
              At DG FutureTech, we offer a suite of specialized ERP solutions designed to address the unique needs of different business functions and industries. From managing your people to streamlining production and strengthening customer relationships, our platforms bring automation, visibility, and control to every corner of your organization. 
            </p>
            <div className={erpStyles.domainGrid}>
              {/* HRMS */}
              <div className={erpStyles.domainCard}>
                <div className={erpStyles.domainCardHeader}>
                  <div className={erpStyles.solutionIcon}>
                    <i className="fas fa-users-cog"></i>
                  </div>
                  <div>
                    <h3 className={erpStyles.domainCardHeaderTitle}>HRMS</h3>
                    <p className={erpStyles.domainCardHeaderSubtitle}>Human Resource Management System</p>
                  </div>
                </div>
                <div className={erpStyles.domainCardBody}>
                  <p className={erpStyles.domainCardDescription}>
                    Our HRMS solution simplifies and automates your entire workforce management process. From recruitment, onboarding, and attendance to payroll, leave management, and performance tracking, everything is handled within one intuitive platform. It reduces administrative burden, ensures compliance, and empowers your HR team to focus on what matters most - your people.
                  </p>
                </div>
              </div>
              {/* HIMS */}
              <div className={erpStyles.domainCard}>
                <div className={erpStyles.domainCardHeader}>
                  <div className={erpStyles.solutionIcon}>
                    <i className="fas fa-hospital"></i>
                  </div>
                  <div>
                    <h3 className={erpStyles.domainCardHeaderTitle}>HIMS</h3>
                    <p className={erpStyles.domainCardHeaderSubtitle}>Hospital Information Management System</p>
                  </div>
                </div>
                <div className={erpStyles.domainCardBody}>
                  <p className={erpStyles.domainCardDescription}>
                    Designed for the healthcare sector, our HIMS integrates every aspect of hospital operations into a single, seamless system. It manages patient records, appointments, billing, pharmacy, lab reports, and inventory with ease. The result is improved patient care, reduced wait times, accurate record-keeping, and smoother coordination across departments.
                  </p>
                </div>
              </div>

              {/* ERP For Manufacturing */}
              <div className={erpStyles.domainCard}>
                <div className={erpStyles.domainCardHeader}>
                  <div className={erpStyles.solutionIcon}>
                    <i className="fas fa-industry"></i>
                  </div>
                  <div>
                    <h3 className={erpStyles.domainCardHeaderTitle}>ERP</h3>
                    <p className={erpStyles.domainCardHeaderSubtitle}>ERP for Manufacturing</p>
                  </div>
                </div>
                <div className={erpStyles.domainCardBody}>
                  <p className={erpStyles.domainCardDescription}>
                    Our manufacturing ERP brings end-to-end control to your production environment. From inventory and procurement to production planning, quality control, and supply chain management, it connects every stage of your operations. Manufacturers gain real-time visibility, reduced wastage, optimized resources, and the agility to meet demand efficiently and on time.
                  </p>
                </div>
              </div>

              {/* CRM */}
              <div className={erpStyles.domainCard}>
                <div className={erpStyles.domainCardHeader}>
                  <div className={erpStyles.solutionIcon}>
                    <i className="fas fa-handshake"></i>
                  </div>
                  <div>
                    <h3 className={erpStyles.domainCardHeaderTitle}>CRM</h3>
                    <p className={erpStyles.domainCardHeaderSubtitle}>Customer Relationship Management</p>
                  </div>
                </div>
                <div className={erpStyles.domainCardBody}>
                  <p className={erpStyles.domainCardDescription}>
                    Our CRM solution helps you build stronger, lasting customer relationships. It centralizes leads, contacts, sales pipelines, and communication history in one place, enabling your team to track opportunities, nurture prospects, and close deals faster. With better insights into customer behavior, you can deliver personalized experiences that drive loyalty and growth.
                  </p>
                </div>
              </div>

              {/* LMS */}
              <div className={erpStyles.domainCard}>
                <div className={erpStyles.domainCardHeader}>
                  <div className={erpStyles.solutionIcon}>
                    <i className="fas fa-laptop-code "></i>
                  </div>
                  <div>
                    <h3 className={erpStyles.domainCardHeaderTitle}>LMS</h3>
                    <p className={erpStyles.domainCardHeaderSubtitle}>Learning Management System</p>
                  </div>
                </div>
                <div className={erpStyles.domainCardBody}>
                  <p className={erpStyles.domainCardDescription}>
                    Our LMS supports both corporate and educational use - from employee onboarding, compliance training, and certification tracking to course delivery, assessments, and student progress tracking. It brings structure and measurability to learning, helping organizations and institutions train, teach, and track outcomes from one centralized platform.
                  </p>
                </div>
              </div>

              {/*Helpdesk*/}
              <div className={erpStyles.domainCard}>
                <div className={erpStyles.domainCardHeader}>
                  <div className={erpStyles.solutionIcon}>
                    <i className="fas fa-headset"></i>
                  </div>
                  <div>
                    <h3 className={erpStyles.domainCardHeaderTitle}>Helpdesk</h3>
                    <p className={erpStyles.domainCardHeaderSubtitle}>IT Helpdesk & Support Management</p>
                  </div>
                </div>
                <div className={erpStyles.domainCardBody}>
                  <p className={erpStyles.domainCardDescription}>
                    Our Helpdesk solution streamlines how your organization manages support requests and resolves issues. From ticket logging and prioritization to SLA tracking, escalation workflows, and knowledge base management, every request is handled efficiently. It ensures faster resolution times, greater accountability, and a consistently better support experience for employees and customers alike.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── 5. USE CASES ── */}
        <UseCasesSection title="Use Cases" useCases={useCases} />

        {/* ── 6. WHY DG FUTURETECH ──*/}
       
        {/* Part 1 - Dark hero banner */}
<section className={erpStyles.whyHeroSection}>
  <div className={erpStyles.whyHeroInner}>
    <p className={erpStyles.whyHeroLabel}>WHY DG FUTURETECH</p>
    <h2 className={erpStyles.whyHeroTitle}>
      ERP that's built to last,<br />not just launched.
    </h2>
    <p className={erpStyles.whyHeroDescription}>
      We don't hand over a system and walk away. We stay involved through
      change management, adoption, and the ongoing tuning that turns
      software into measurable results.
    </p>
    <div className={erpStyles.whyHeroBtns}>
      <a
        href="https://calendly.com/mitali-phatak-dgfuturetech/30min"
        target="_blank"
        rel="noopener noreferrer"
        className={erpStyles.whyHeroBtnPrimary}
      >
        Talk to our team
      </a>
      <a
        href="/en/erp-solutions#our-solutions"
        className={erpStyles.whyHeroBtnSecondary}
      >
        See what we deliver
      </a>
    </div>
  </div>
</section>
 
{/* Part 2 - Partner section */}
<section className={erpStyles.whyPartnerSection}>
  <div className={erpStyles.whyPartnerInner}>
    <p className={erpStyles.whyPartnerLabel}>A PARTNER, NOT A VENDOR</p>
    <h2 className={erpStyles.whyPartnerTitle}>
      The kind of partner who answers when you call
    </h2>
    <p className={erpStyles.whyPartnerIntro}>
      With offices in Pune, Tokyo, and San Francisco, we combine global
      delivery standards with the responsiveness of a team that's actually
      reachable. Here's what that means for you:
    </p>
    <ul className={erpStyles.whyPartnerList}>
      <li className={erpStyles.whyPartnerItem}>
        <span className={erpStyles.whyPartnerCheck}>✓</span>
        <span>
          <strong>One team through the whole journey</strong> - from
          scoping to go-live to the quiet months after.
        </span>
      </li>
      <li className={erpStyles.whyPartnerItem}>
        <span className={erpStyles.whyPartnerCheck}>✓</span>
        <span>
          <strong>Global standards, local responsiveness</strong> -
          delivery that holds up internationally without the distance.
        </span>
      </li>
      <li className={erpStyles.whyPartnerItem}>
        <span className={erpStyles.whyPartnerCheck}>✓</span>
        <span>
          <strong>Your success as the actual goal</strong> - we measure
          ourselves by your results, not by hours billed.
        </span>
      </li>
    </ul>
  </div>
</section>
 
{/* Part 3 - Quote */}
<section className={erpStyles.whyQuoteSection}>
  <blockquote className={erpStyles.whyQuote}>
    "The goal isn't to install software. It's to make the work go better."
  </blockquote>
  <p className={erpStyles.whyQuoteAttrib}>
    - HOW WE APPROACH EVERY ERP PROJECT
  </p>
</section>

        <div><ServiceSection /></div>

        <CTASection
          title="Ready to unify your business operations?"
          buttonText="Book a Consultation"
          buttonLink="https://calendly.com/mitali-phatak-dgfuturetech/30min"
          target="_blank"
        />

      </div>
    </>
  );
};

export default ERPSolutions;
